import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import SchemaOrg from '../../../components/base/SchemaOrg';

export default function EndoscopePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqItems = [
    {
      question: '什麼情況需要做內視鏡檢查？',
      answer: '當排水經常堵塞、排水速度異常緩慢、有異味但找不到原因、懷疑管線破損或老化、購買中古屋想了解管線狀況時，建議進行內視鏡檢查。這能精準找出問題根源。'
    },
    {
      question: '內視鏡檢查能看到什麼？',
      answer: '可以清楚看到管線內部狀況，包括：油脂堆積程度、異物堵塞位置、管線破損裂縫、接縫處滲漏、樹根入侵、管線變形等。我們會錄影記錄，讓您清楚了解問題。'
    },
    {
      question: '檢查會破壞管線嗎？',
      answer: '完全不會。內視鏡是非破壞性檢查，只需從排水孔或檢查口伸入攝影機，不需要挖掘或破壞管線。檢查過程安全無害，不會影響管線結構。'
    },
    {
      question: '內視鏡檢查需要多久時間？',
      answer: '一般住宅管線檢查約1-2小時，視管線長度和複雜度而定。檢查完成後會立即提供影像資料和問題分析報告。'
    },
    {
      question: '檢查費用如何計算？',
      answer: '內視鏡檢查費用約$8,000，包含完整檢查、影像記錄、問題分析報告。如果同時進行疏通或維修，檢查費用可折抵部分工程款。'
    },
    {
      question: '檢查後會提供報告嗎？',
      answer: '是的，我們會提供完整的檢查報告，包含：影像記錄光碟、問題點標示圖、狀況說明、建議處理方案。您可以保存作為日後維護參考。'
    }
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/services/endoscope#service`,
      name: '內視鏡檢查服務',
      description: '專業管線內視鏡檢查服務，精準定位問題根源。提供完整影像記錄與分析報告，對症下藥處理。',
      provider: {
        '@type': 'LocalBusiness',
        name: '永業環保',
        telephone: '0800-281-281',
        url: siteUrl
      },
      areaServed: [
        { '@type': 'City', name: '桃園市' },
        { '@type': 'City', name: '新北市' },
        { '@type': 'City', name: '台北市' },
        { '@type': 'City', name: '新竹市' },
        { '@type': 'City', name: '苗栗縣' }
      ],
      offers: {
        '@type': 'Offer',
        price: '8000',
        priceCurrency: 'TWD'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/services/endoscope#faqpage`,
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/services/endoscope#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '服務項目', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: '內視鏡檢查', item: `${siteUrl}/services/endoscope` }
      ]
    }
  ];

  useEffect(() => {
    const pageTitle = '管線內視鏡檢查｜精密管道診斷｜永業環保';
    const pageDesc = '永業環保提供專業管線內視鏡檢查服務，精準定位問題根源。不破壞裝潢，高解析度攝影，提供完整檢測報告，服務桃園、新北、台北、新竹、苗栗地區，20年專業經驗。';
    const pageKeywords = '內視鏡檢查,管線檢測,管道診斷,管線破損,漏水檢測,桃園管線';
    const pageUrl = `${siteUrl}/services/endoscope`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);

    window.scrollTo(0, 0);
  }, [siteUrl]);

  const features = [
    {
      icon: 'ri-eye-line',
      title: '精密檢測',
      description: '高解析度攝影機，清楚呈現管線內部狀況，不放過任何細節'
    },
    {
      icon: 'ri-shield-check-line',
      title: '非破壞性',
      description: '不需破壞牆面或地板，即可深入檢查管線內部問題'
    },
    {
      icon: 'ri-file-text-line',
      title: '詳細報告',
      description: '提供完整檢測影片與照片，附上專業分析與建議'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '節省成本',
      description: '精準找出問題點，避免盲目施工，節省維修費用'
    }
  ];

  const detectable = [
    {
      category: '堵塞問題',
      icon: 'ri-close-circle-line',
      items: [
        { name: '油脂堆積', description: '廚房管線常見的油脂附著' },
        { name: '異物堵塞', description: '毛髮、雜物等造成的阻塞' },
        { name: '樹根入侵', description: '戶外管線被樹根穿透' },
        { name: '淤泥沉積', description: '長期累積的泥沙與污垢' }
      ]
    },
    {
      category: '結構問題',
      icon: 'ri-alert-line',
      items: [
        { name: '管線破裂', description: '老化或外力造成的裂縫' },
        { name: '接縫脫落', description: '管線連接處鬆脫或錯位' },
        { name: '管壁腐蝕', description: '化學物質侵蝕管線內壁' },
        { name: '變形塌陷', description: '地層下陷導致管線變形' }
      ]
    },
    {
      category: '安裝問題',
      icon: 'ri-tools-line',
      items: [
        { name: '坡度不當', description: '排水坡度設計不良' },
        { name: '管徑不符', description: '管線口徑選擇錯誤' },
        { name: '轉彎過多', description: '管線轉折角度不當' },
        { name: '施工瑕疵', description: '安裝時的施工缺失' }
      ]
    }
  ];

  const process = [
    {
      step: '1',
      title: '預約檢測',
      description: '聯繫我們說明問題，安排檢測時間',
      icon: 'ri-calendar-line'
    },
    {
      step: '2',
      title: '現場評估',
      description: '技師到場了解狀況，確認檢測範圍',
      icon: 'ri-map-pin-line'
    },
    {
      step: '3',
      title: '設備準備',
      description: '準備內視鏡設備，調整攝影參數',
      icon: 'ri-settings-line'
    },
    {
      step: '4',
      title: '深入檢測',
      description: '將內視鏡探頭深入管線，全程錄影',
      icon: 'ri-search-eye-line'
    },
    {
      step: '5',
      title: '問題分析',
      description: '分析檢測影像，找出問題根源',
      icon: 'ri-file-search-line'
    },
    {
      step: '6',
      title: '報告說明',
      description: '提供檢測報告與維修建議',
      icon: 'ri-presentation-line'
    }
  ];

  const scenarios = [
    {
      icon: 'ri-water-flash-line',
      title: '排水不順',
      description: '水流緩慢或經常堵塞，但不知道原因',
      solution: '內視鏡檢查可找出堵塞位置與原因'
    },
    {
      icon: 'ri-drop-line',
      title: '漏水問題',
      description: '牆壁或地板滲水，懷疑管線破裂',
      solution: '精準定位破損點，避免大範圍破壞'
    },
    {
      icon: 'ri-bug-line',
      title: '異味產生',
      description: '排水孔傳出惡臭，可能有異物腐爛',
      solution: '檢查管線內部，找出異味來源'
    },
    {
      icon: 'ri-sound-module-line',
      title: '異常聲響',
      description: '排水時有怪聲，懷疑管線有問題',
      solution: '檢測管線結構，確認是否有損壞'
    },
    {
      icon: 'ri-home-line',
      title: '購屋檢測',
      description: '購買中古屋前，想了解管線狀況',
      solution: '完整檢測報告，作為購屋參考'
    },
    {
      icon: 'ri-building-line',
      title: '定期檢查',
      description: '老舊建築定期檢查管線健康狀況',
      solution: '預防性檢測，及早發現潛在問題'
    }
  ];

  const reportContent = [
    {
      icon: 'ri-video-line',
      title: '完整影片',
      description: '提供檢測全程影片，可重複觀看'
    },
    {
      icon: 'ri-image-line',
      title: '關鍵照片',
      description: '擷取重要畫面，標註問題位置'
    },
    {
      icon: 'ri-map-line',
      title: '位置標示',
      description: '標明問題點的確切位置與深度'
    },
    {
      icon: 'ri-file-list-line',
      title: '問題清單',
      description: '列出所有發現的問題與嚴重程度'
    },
    {
      icon: 'ri-lightbulb-line',
      title: '維修建議',
      description: '提供專業的維修方案與優先順序'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '費用評估',
      description: '預估維修所需的時間與費用'
    }
  ];

  const advantages = [
    '精準定位問題，避免盲目施工',
    '不需破壞裝潢，節省修復成本',
    '高解析度影像，清楚呈現細節',
    '完整記錄存檔，方便日後追蹤',
    '專業分析報告，提供維修依據',
    '預防性檢測，及早發現問題'
  ];

  const faqs = [
    {
      question: '什麼情況需要做內視鏡檢查？',
      answer: '當您遇到排水不順、經常堵塞、漏水、異味、或購買中古屋想了解管線狀況時，都建議進行內視鏡檢查。這項檢測可以精準找出問題根源，避免盲目施工造成不必要的破壞與費用。'
    },
    {
      question: '內視鏡可以檢查多深的管線？',
      answer: '我們的專業內視鏡設備可以檢測深度達 30-50 公尺，涵蓋大部分住宅與商業建築的管線系統。實際檢測深度會依管線狀況、轉彎數量等因素而定，我們會在現場評估後告知。'
    },
    {
      question: '檢查會不會損壞管線？',
      answer: '完全不會！內視鏡檢查是非破壞性檢測，探頭外層有保護套，不會刮傷或損壞管線。而且不需要破壞牆面或地板，就能深入檢查管線內部，是最安全的檢測方式。'
    },
    {
      question: '檢查需要多少時間？',
      answer: '一般住宅管線檢查約需 1-2 小時，包含設備準備、檢測、分析等時間。如果是大型建築或需要檢查多條管線，時間會相對較長。我們會在現場評估後告知預估時間。'
    },
    {
      question: '檢查報告包含哪些內容？',
      answer: '我們會提供完整的檢測影片、關鍵照片、問題位置標示、問題清單、嚴重程度評估、維修建議，以及費用預估。報告內容詳盡，讓您清楚了解管線狀況，作為維修決策的依據。'
    },
    {
      question: '檢查後一定要維修嗎？',
      answer: '不一定。檢查的目的是了解管線狀況，如果發現的問題不嚴重，可以先觀察或定期追蹤。我們會依問題的嚴重程度給予建議，最終決定權在您手上。我們不會強迫推銷維修服務。'
    }
  ];

  const relatedServices = [
    {
      title: '水管水刀疏通',
      description: '高壓水刀技術，快速疏通各種管線堵塞',
      icon: 'ri-water-flash-line',
      link: '/services/pipe-cleaning'
    },
    {
      title: '水溝清洗',
      description: '專業清理排水溝渠，預防淹水問題',
      icon: 'ri-contrast-drop-line',
      link: '/services/drain-cleaning'
    },
    {
      title: '水塔清洗',
      description: '定期清洗水塔，確保飲用水安全',
      icon: 'ri-drop-line',
      link: '/services/water-tank'
    }
  ];

  return (
    <>
      <SchemaOrg schema={schemas} />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-50 via-white to-violet-50 pt-32 pb-20 md:pt-32 md:pb-20 py-20">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/356287b51bd883a605bd68cc642b3cb6.jpg')] bg-cover bg-center opacity-5"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-search-eye-line"></i>
                <span>精密管線檢測技術</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 font-serif">
                內視鏡檢查服務
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                採用高解析度內視鏡設備，深入管線內部精密檢測<br className="hidden md:block" />
                不破壞裝潢，精準找出問題根源，提供完整檢測報告
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  <span>預約檢測服務</span>
                </Link>
                <a
                  href="tel:0800-281-281"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-indigo-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300 border-2 border-indigo-600 whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>0800-281-281</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  內視鏡檢查四大優勢
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  為什麼選擇內視鏡檢測？
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-indigo-50 to-white p-8 rounded-xl border border-indigo-100 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${feature.icon} text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Detectable Problems */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  可檢測問題
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  內視鏡能發現哪些管線問題？
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {detectable.map((category, index) => (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-lg flex items-center justify-center">
                        <i className={`${category.icon} text-2xl text-indigo-600`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {category.category}
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="border-l-2 border-indigo-200 pl-4">
                          <h4 className="font-bold text-gray-900 mb-1">
                            {item.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {item.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  檢測流程
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  六大步驟，完整檢測您的管線
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {process.map((step, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-indigo-600 transition-colors duration-300">
                          <i className={`${step.icon} text-xl text-indigo-600 group-hover:text-white transition-colors duration-300`}></i>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Scenarios */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-indigo-50 via-white to-violet-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  適用場景
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  這些情況，建議進行內視鏡檢查
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {scenarios.map((scenario, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <i className={`${scenario.icon} text-2xl text-indigo-600`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {scenario.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {scenario.description}
                    </p>
                    <div className="flex items-start gap-2 text-indigo-600 text-sm">
                      <i className="ri-arrow-right-s-line mt-0.5"></i>
                      <span>{scenario.solution}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Report Content */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  檢測報告說明
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  完整詳盡的檢測報告，讓您清楚了解管線狀況
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {reportContent.map((item, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-indigo-600 transition-colors duration-300">
                      <i className={`${item.icon} text-xl text-indigo-600 group-hover:text-white transition-colors duration-300`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-br from-indigo-50 to-violet-50 p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  為什麼需要內視鏡檢查？
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {advantages.map((advantage, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700">{advantage}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  常見問題
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  關於內視鏡檢查的疑問，這裡都有解答
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="bg-white p-4 md:p-6 rounded-xl border border-gray-100 hover:border-indigo-200 transition-all duration-300 group shadow-lg"
                  >
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <h3 className="text-base md:text-lg font-bold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      <i className="ri-arrow-down-s-line text-2xl text-indigo-600 group-open:rotate-180 transition-transform duration-300 flex-shrink-0"></i>
                    </summary>
                    <p className="mt-4 text-sm md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  相關服務
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  更多專業服務，滿足您的需求
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 group border border-gray-100 hover:border-indigo-200"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-3xl text-indigo-600`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2 text-indigo-600 font-medium">
                      <span>了解更多</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-indigo-600 to-violet-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20technology%20pattern%20with%20digital%20lines%20and%20inspection%20concept%2C%20minimalist%20design%2C%20soft%20gradient%20background%2C%20professional%20and%20modern%20atmosphere%2C%20high%20tech%20abstract%20style%2C%20high%20quality%20digital%20art%2C%20indigo%20and%20violet%20color%20scheme%2C%20precision%20and%20accuracy%20theme&width=1920&height=400&seq=endoscope-cta-bg&orientation=landscape')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">
                精準檢測，找出問題根源
              </h2>
              <p className="text-lg md:text-xl mb-8 opacity-90">
                不破壞裝潢，深入管線內部檢查<br className="hidden md:block" />
                專業團隊、先進設備，提供完整檢測報告
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-indigo-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  <span>立即預約</span>
                </Link>
                <a
                  href="tel:0800-281-281"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent text-white px-8 py-4 rounded-lg font-medium hover:bg-white/10 transition-all duration-300 border-2 border-white whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>電話諮詢</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
